import React, { useState } from "react";
import { format } from "date-fns";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { LogEntry, LEVEL_TAG_VARIANT } from "./LogsData";

interface Props {
  entry: LogEntry | null;
  onClose: () => void;
}

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="grid grid-cols-[110px_1fr] gap-3 items-start py-1.5">
    <span className="text-xs uppercase tracking-wide text-semantic-text-secondary pt-0.5">
      {label}
    </span>
    <div className="text-sm text-semantic-text-primary min-w-0 break-words">
      {children}
    </div>
  </div>
);

const LogEntryModal: React.FC<Props> = ({ entry, onClose }) => {
  const [copied, setCopied] = useState(false);

  const copyMessage = async () => {
    if (!entry) return;
    try {
      await navigator.clipboard.writeText(entry.message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable in non-secure contexts; silently ignore
    }
  };

  return (
    <GenericModal
      isOpen={Boolean(entry)}
      onClose={onClose}
      title="Log entry"
      customWidth="780px"
      maxHeight="85vh"
    >
      {entry && (
        <div className="flex flex-col gap-1">
          <FieldRow label="Time">
            <span className="font-mono">
              {format(new Date(entry.timestamp), "yyyy-MM-dd HH:mm:ss.SSS")}
            </span>
          </FieldRow>

          <FieldRow label="Level">
            <GenericTag
              label={entry.level}
              variant={LEVEL_TAG_VARIANT[entry.level]}
              size="small"
              rounded="full"
              minWidth="60px"
            />
          </FieldRow>

          <FieldRow label="Reflector">
            <div className="flex flex-col gap-0.5 font-mono text-xs">
              <span className="text-semantic-text-primary">
                {entry.reflectorId || "—"}
              </span>
              {entry.reflectorName && (
                <span className="text-semantic-text-secondary">
                  {entry.reflectorName}
                </span>
              )}
              {entry.reflectorUuid && (
                <span className="text-semantic-text-secondary">
                  {entry.reflectorUuid}
                </span>
              )}
            </div>
          </FieldRow>

          {entry.source && (
            <FieldRow label="Source">
              <GenericTag
                label={entry.source}
                variant="neutral"
                size="small"
                rounded="small"
              />
            </FieldRow>
          )}

          <div className="mt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs uppercase tracking-wide text-semantic-text-secondary">
                Message
              </span>
              <button
                type="button"
                onClick={copyMessage}
                className="text-xs px-2 py-1 rounded-sm border border-semantic-border-secondary text-semantic-text-secondary hover:bg-semantic-state-active"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <pre className="font-mono text-xs text-semantic-text-primary bg-semantic-bg-primary-alt border border-semantic-border-primary rounded-md p-3 whitespace-pre-wrap break-words max-h-[50vh] overflow-auto">
              {entry.message}
            </pre>
          </div>
        </div>
      )}
    </GenericModal>
  );
};

export default LogEntryModal;
